import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, EditorState } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { CellSelection } from "@tiptap/pm/tables";
import { Node } from "prosemirror-model";
import { countColumnsInActiveTable, countRowsInActiveTable } from "../lib/tableRowAndColumnCount";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableControls: {
      setSelectedCell: (tablePos: number, cellPos: number) => ReturnType;
      clearSelectedCell: () => ReturnType;
    };
  }
}

export const TableControls = Extension.create({
  name: "tableControls",

  addStorage() {
    return {
      selectedTablePos: -1,
      selectedCellPos: -1
    };
  },

  addCommands() {
    return {
      setSelectedCell: (tablePos: number, cellPos: number) => () => {
        this.storage.selectedTablePos = tablePos;
        this.storage.selectedCellPos = cellPos;
        return true;
      },
      clearSelectedCell: () => () => {
        this.storage.selectedTablePos = -1;
        this.storage.selectedCellPos = -1;
        return true;
      }
    };
  },

  addProseMirrorPlugins() {
    const key = new PluginKey("tableControls");
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey("tableControlsSelection"),
        state: {
          init: () => null,
          apply: (tr, oldState) => {
            if (!tr.selectionSet && !tr.docChanged) return oldState;

            const { selection } = tr;
            const { $from } = selection;

            let cellPos = -1;
            let tablePos = -1;

            for (let depth = $from.depth; depth > 0; depth--) {
              const node = $from.node(depth);
              const pos = $from.start(depth);

              if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
                cellPos = pos - 1;
              }

              if (node.type.name === "table") {
                tablePos = pos - 1;
                break;
              }
            }

            if (cellPos !== -1 && tablePos !== -1) {
              editor.commands.setSelectedCell(tablePos, cellPos);
            } else {
              editor.commands.clearSelectedCell();
            }

            return null;
          }
        }
      }),

      new Plugin({
        key,
        state: {
          init: () => DecorationSet.empty,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          apply: (tr, oldState) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { selectedTablePos, selectedCellPos } = editor.storage.tableControls;
            if (selectedCellPos === -1) {
              return DecorationSet.empty;
            }

            let node;
            try {
              node = tr.doc.nodeAt(selectedCellPos);
            } catch (error) {
              console.error("Error getting node at selected cell pos", error);
              return DecorationSet.empty;
            }
            if (!node) return DecorationSet.empty;

            const createBtn = (className: string, style: string, onClick: () => void, text = "+", isDelete = false) => {
              const btn = document.createElement("button");
              btn.type = "button";
              btn.className = className;
              btn.textContent = text;
              const width = isDelete ? "auto" : "18px";
              const padding = isDelete ? "padding:2px 6px;" : "";
              const fontSize = isDelete ? "9px" : "12px";
              const baseStyle = ` width:${width};height:18px;border:1px solid;border-radius:${
                isDelete ? "8px" : "50%"
              };font-size:${fontSize};line-height:14px;cursor:pointer;z-index:10;box-shadow:0 1px 3px rgba(0,0,0,0.1);font-weight:bold;${padding}white-space:nowrap;`;
              const deleteStyle = isDelete
                ? "border-color:#ef4444;background:#fee2e2;color:#dc2626;"
                : "border-color:#d1d5db;background:#ffffff;color:#374151;";
              btn.setAttribute("style", style + baseStyle + deleteStyle);
              btn.addEventListener("mousedown", (e) => e.preventDefault());
              btn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
              });
              return btn;
            };

            const rowCount = countRowsInActiveTable(editor);
            const colCount = countColumnsInActiveTable(editor);

            const rowAfterBtn = createBtn(
              "tiptap-add-row-btn",
              "position:absolute;left:50%;bottom:-10px;transform:translateX(-50%);",
              () => editor.chain().focus().addRowAfter().run()
            );

            const rowBeforeBtn = createBtn(
              "tiptap-add-row-before-btn",
              "position:absolute;left:50%;top:10px;transform:translate(-50%,-100%);",
              () => editor.chain().focus().addRowBefore().run()
            );

            const colAfterBtn = createBtn(
              "tiptap-add-col-btn",
              "position:absolute;top:50%;right:-10px;transform:translateY(-50%);",
              () => editor.chain().focus().addColumnAfter().run()
            );

            const colBeforeBtn = createBtn(
              "tiptap-add-col-before-btn",
              "position:absolute;top:50%;left:10px;transform:translate(-100%,-50%);",
              () => editor.chain().focus().addColumnBefore().run()
            );

            const deleteColBtn = createBtn(
              "tiptap-delete-col-btn",
              "position:absolute;top:5px;left:50%;transform:translateX(-50%);",
              () => {
                colCount > 1 ? editor.chain().focus().deleteColumn().run() : editor.chain().focus().deleteTable().run();
              },
              "-",
              true
            );

            const deleteRowBtn = createBtn(
              "tiptap-delete-row-btn",
              "position:absolute;right:10px;top:50%;transform:translateY(-50%);",
              () =>
                rowCount > 1 ? editor.chain().focus().deleteRow().run() : editor.chain().focus().deleteTable().run(),
              "-",
              true
            );

            const decorations = [
              Decoration.widget(selectedCellPos + node.nodeSize - 1, rowBeforeBtn, { side: -1 }),
              Decoration.widget(selectedCellPos + node.nodeSize - 1, rowAfterBtn, { side: -1 }),
              Decoration.widget(selectedCellPos + node.nodeSize - 1, colBeforeBtn, { side: -1 }),
              Decoration.widget(selectedCellPos + node.nodeSize - 1, colAfterBtn, { side: -1 }),
              Decoration.widget(selectedCellPos + node.nodeSize - 1, deleteColBtn, { side: -1 }),
              Decoration.widget(selectedCellPos + node.nodeSize - 1, deleteRowBtn, { side: -1 })
            ];

            return DecorationSet.create(tr.doc, decorations);
          }
        },
        props: {
          decorations: (state) => key.getState(state)
        }
      }),

      new Plugin({
        key: new PluginKey("tableControlsKeyboard"),
        props: {
          handleKeyDown: (view, event) => {
            if (event.key !== "Backspace" && event.key !== "Delete") return false;

            const { selection } = view.state;

            if (!(selection instanceof CellSelection)) return false;

            const cellSelection = selection;

            const cells: { pos: number; node: Node }[] = [];
            cellSelection.forEachCell((cell, pos: number) => {
              cells.push({ pos, node: cell });
            });

            if (cells.length === 0) return false;

            const isWholeRowSelected = isWholeRowSelectedFn(view.state, cells);
            const isWholeColumnSelected = isWholeColumnSelectedFn(view.state, cells);

            if (isWholeRowSelected) {
              event.preventDefault();
              editor.chain().focus().deleteRow().setTextSelection(0).run();
              return true;
            }

            if (isWholeColumnSelected) {
              event.preventDefault();
              editor.chain().focus().deleteColumn().setTextSelection(0).run();
              return true;
            }

            return false;
          }
        }
      })
    ];

    function isWholeRowSelectedFn(state: EditorState, selectedCells: { pos: number; node: Node }[]) {
      if (selectedCells.length === 0) return false;

      const firstCellPos = selectedCells[0].pos;
      const $pos = state.doc.resolve(firstCellPos);

      let tableNode: Node | null = null;
      let tablePos = -1;
      for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === "table") {
          tableNode = node;
          tablePos = $pos.start(depth);
          break;
        }
      }

      if (!tableNode) return false;

      let rowIndex = -1;
      let cellIndex = 0;
      tableNode.forEach((rowNode: Node, rowOffset: number) => {
        if (rowNode.type.name === "tableRow") {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          let cellCount = 0;
          rowNode.forEach((cellNode: Node, cellOffset: number) => {
            const cellPos = tablePos + rowOffset + cellOffset + 1;
            if (cellPos === firstCellPos) {
              rowIndex = cellIndex;
            }
            cellCount++;
          });
          if (rowIndex === -1) cellIndex++;
        }
      });

      if (rowIndex === -1) return false;

      let rowCellCount = 0;
      let currentRowIndex = 0;
      tableNode.forEach((rowNode: Node) => {
        if (rowNode.type.name === "tableRow") {
          if (currentRowIndex === rowIndex) {
            rowNode.forEach(() => rowCellCount++);
          }
          currentRowIndex++;
        }
      });

      return selectedCells.length === rowCellCount;
    }

    function isWholeColumnSelectedFn(state: EditorState, selectedCells: { pos: number; node: Node }[]) {
      if (selectedCells.length === 0) return false;

      const firstCellPos = selectedCells[0].pos;
      const $pos = state.doc.resolve(firstCellPos);

      let tableNode: Node | null = null;
      let tablePos = -1;
      for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === "table") {
          tableNode = node;
          tablePos = $pos.start(depth);
          break;
        }
      }

      if (!tableNode) return false;

      let columnIndex = -1;
      let rowCount = 0;
      tableNode.forEach((rowNode: Node, rowOffset: number) => {
        if (rowNode.type.name === "tableRow") {
          let cellIndex = 0;
          rowNode.forEach((cellNode: Node, cellOffset: number) => {
            const cellPos = tablePos + rowOffset + cellOffset + 1;
            if (cellPos === firstCellPos) {
              columnIndex = cellIndex;
            }
            cellIndex++;
          });
          rowCount++;
        }
      });

      if (columnIndex === -1) return false;

      return selectedCells.length === rowCount;
    }
  },

  onCreate() {
    const style = document.createElement("style");
    style.textContent = `
      .ProseMirror td, .ProseMirror th {
        position: relative;
        overflow: visible;
        word-break: break-word;
        overflow-wrap: anywhere;
        min-width: 0;
      }
      .ProseMirror table {
        overflow: visible;
        width: 100%;
      }
      .ProseMirror tr {
        overflow: visible;
      }
      .ProseMirror table a {
        word-break: break-all;
        overflow-wrap: anywhere;
      }
      .tiptap-add-row-btn,
      .tiptap-add-row-before-btn,
      .tiptap-add-col-btn,
      .tiptap-add-col-before-btn,
      .tiptap-delete-row-btn,
      .tiptap-delete-col-btn {
        pointer-events: auto;
        transition: all 0.2s ease;
      }
      .tiptap-delete-row-btn:hover,
      .tiptap-delete-col-btn:hover {
        background: #fecaca !important;
        transform: scale(1.05);
      }
      
      /* Highlight row on delete button hover */
      .tiptap-delete-row-btn:hover ~ tr,
      .tiptap-delete-row-btn:hover {
        position: relative;
      }
      
      /* Highlight column on delete button hover */
      .tiptap-delete-col-btn:hover {
        position: relative;
      }
    `;
    document.head.appendChild(style);
  }
});
